var NEWTON_ITERATIONS = 4;
var NEWTON_MIN_SLOPE = 0.001;
var SUBDIVISION_PRECISION = 0.0000001;
var SUBDIVISION_MAX_ITERATIONS = 10;
var kSplineTableSize = 11;
var kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);
var float32ArraySupported = typeof Float32Array === 'function';
var A = function (aA1, aA2) { return 1.0 - 3.0 * aA2 + 3.0 * aA1; };
var B = function (aA1, aA2) { return 3.0 * aA2 - 6.0 * aA1; };
var C = function (aA1) { return 3.0 * aA1; };
var calcBezier = function (aT, aA1, aA2) {
    return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
};
var getSlope = function (aT, aA1, aA2) {
    return 3.0 * A(aA1, aA2) * aT * aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
};
var binarySubdivide = function (aX, aA, aB, mX1, mX2) {
    var currentX, currentT, i = 0;
    do {
        currentT = aA + (aB - aA) / 2.0;
        currentX = calcBezier(currentT, mX1, mX2) - aX;
        if (currentX > 0.0) {
            aB = currentT;
        }
        else {
            aA = currentT;
        }
    } while (Math.abs(currentX) > SUBDIVISION_PRECISION &&
        ++i < SUBDIVISION_MAX_ITERATIONS);
    return currentT;
};
var newtonRaphsonIterate = function (aX, aGuessT, mX1, mX2) {
    for (var i = 0; i < NEWTON_ITERATIONS; ++i) {
        var currentSlope = getSlope(aGuessT, mX1, mX2);
        if (currentSlope === 0.0) {
            return aGuessT;
        }
        var currentX = calcBezier(aGuessT, mX1, mX2) - aX;
        aGuessT -= currentX / currentSlope;
    }
    return aGuessT;
};
var bezier = function (mX1, mY1, mX2, mY2) {
    if (!(0 <= mX1 && mX1 <= 1 && 0 <= mX2 && mX2 <= 1)) {
        throw new Error('bezier x values must be in [0, 1] range');
    }
    var sampleValues = float32ArraySupported
        ? new Float32Array(kSplineTableSize)
        : new Array(kSplineTableSize);
    if (mX1 !== mY1 || mX2 !== mY2) {
        for (var i = 0; i < kSplineTableSize; ++i) {
            sampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
        }
    }
    var getTForX = function (aX) {
        var intervalStart = 0.0;
        var currentSample = 1;
        var lastSample = kSplineTableSize - 1;
        for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
            intervalStart += kSampleStepSize;
        }
        --currentSample;
        var dist = (aX - sampleValues[currentSample]) /
            (sampleValues[currentSample + 1] - sampleValues[currentSample]);
        var guessForT = intervalStart + dist * kSampleStepSize;
        var initialSlope = getSlope(guessForT, mX1, mX2);
        if (initialSlope >= NEWTON_MIN_SLOPE) {
            return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
        }
        else if (initialSlope === 0.0) {
            return guessForT;
        }
        else {
            return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize, mX1, mX2);
        }
    };
    return function (x) {
        if (mX1 === mY1 && mX2 === mY2) {
            return x;
        }
        if (x === 0) {
            return 0;
        }
        if (x === 1) {
            return 1;
        }
        return calcBezier(getTForX(x), mY1, mY2);
    };
};
var noop = function () { };
var getScheduler = function (scheduler) {
    if (!scheduler) {
        var canRaf = typeof window !== 'undefined' && window.requestAnimationFrame;
        return canRaf ? rafScheduler() : timeoutScheduler();
    }
    if (typeof scheduler.next !== 'function')
        throw new Error('Scheduler is supposed to have next(cb) function');
    if (typeof scheduler.cancel !== 'function')
        throw new Error('Scheduler is supposed to have cancel(handle) function');
    return scheduler;
};
var rafScheduler = function () { return ({
    next: window.requestAnimationFrame.bind(window),
    cancel: window.cancelAnimationFrame.bind(window),
}); };
var timeoutScheduler = function () { return ({
    next: function (cb) { return setTimeout(cb, 1000 / 60); },
    cancel: function (id) { return clearTimeout(id); },
}); };
export var animate = function (source, target, options) {
    var start = Object.create(null);
    var diff = Object.create(null);
    var animations = {
        ease: bezier(0.25, 0.1, 0.25, 1),
        easeIn: bezier(0.42, 0, 1, 1),
        easeOut: bezier(0, 0, 0.58, 1),
        easeInOut: bezier(0.42, 0, 0.58, 1),
        linear: bezier(0, 0, 1, 1),
    };
    options = options || {};
    var easing = typeof options.easing === 'function'
        ? options.easing
        : animations[options.easing];
    if (!easing) {
        if (options.easing) {
            console.warn('Unknown easing function in amator: ' + options.easing);
        }
        easing = animations.ease;
    }
    var step = typeof options.step === 'function' ? options.step : noop;
    var done = typeof options.done === 'function' ? options.done : noop;
    var scheduler = getScheduler(options.scheduler);
    var keys = Object.keys(target);
    keys.forEach(function (key) {
        start[key] = source[key];
        diff[key] = target[key] - source[key];
    });
    var durationInMs = options.duration || 400;
    var durationInFrames = Math.max(1, durationInMs * 0.06);
    var previousAnimationId;
    var frame = 0;
    var setValues = function (t) {
        keys.forEach(function (key) {
            source[key] = diff[key] * t + start[key];
        });
    };
    var loop = function () {
        var t = easing(frame / durationInFrames);
        frame += 1;
        setValues(t);
        if (frame <= durationInFrames) {
            previousAnimationId = scheduler.next(loop);
            step(source);
        }
        else {
            previousAnimationId = 0;
            setTimeout(function () {
                done(source);
            }, 0);
        }
    };
    previousAnimationId = scheduler.next(loop);
    var cancel = function () {
        scheduler.cancel(previousAnimationId);
        previousAnimationId = 0;
    };
    return { cancel: cancel };
};
